// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getComputedVariantProduct,
    getProductState,
    getProductStateByPath,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Page from "@insite/mobius/Page";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadEvent from "@mcmc/App/Handlers/Pages/EventRegistration/LoadEvent";
import CurrentCategory from "@mcmc/Overrides/Components/CurrentCategory";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    let eventPath = "";
    let childEventNumber = "";
    if (location && location.search) {
        const parsedQuery = parseQueryString<{ eventPath: string }>(location.search);
        const pathParts = parsedQuery.eventPath?.split("?");
        eventPath = pathParts[0];
        childEventNumber = pathParts[1] && parseQueryString<{ option: string }>(pathParts[1]).option;
    }
    const eventState = getProductStateByPath(state, eventPath);
    const variantProductState = getProductState(state, state.pages.productDetails.selectedProductId);
    const computedVariantProduct = getComputedVariantProduct(eventState, variantProductState);
    return {
        eventPath,
        childEventNumber,
        eventState: computedVariantProduct,
        parentEventState: eventState,
        lastPath: state.pages.productDetails.lastProductPath,
        lastStyledOption: state.pages.productDetails.lastStyledOption,
        eventInfo:
            computedVariantProduct.value && state.pages.productDetails.productInfosById
                ? state.pages.productDetails.productInfosById[computedVariantProduct.value.id]
                : undefined,
    };
};
const mapDispatchToProps = { loadEvent };
const postfixBreadcrumb = {
    children: translate("Registration"),
    href: "",
};
type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & PageProps;
interface State {}
interface EventRegistrationPageStyles {
    spinnerWrapper?: InjectableCss;
}
const styles: EventRegistrationPageStyles = {
    spinnerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 300px;
        `,
    },
};
class EventRegistrationPage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {};
    }
    UNSAFE_componentWillMount() {
        this.loadEventIfNeeded();
    }
    componentDidUpdate(): void {
        this.loadEventIfNeeded();
    }
    loadEventIfNeeded() {
        const { eventState, eventInfo, loadEvent, eventPath, lastPath, childEventNumber, lastStyledOption } =
            this.props;
        if (
            !eventPath ||
            eventState.isLoading ||
            (eventState.value && eventInfo && eventPath === lastPath && childEventNumber === lastStyledOption)
        ) {
            return;
        }
        loadEvent({ path: eventPath, styledOption: childEventNumber });
    }
    render() {
        const { eventState, eventInfo, eventPath } = this.props;
        if (eventState.isLoading) {
            return (
                <StyledWrapper {...styles.spinnerWrapper}>
                    <LoadingSpinner />
                </StyledWrapper>
            );
        }
        if (!eventState.value || !eventInfo || !eventPath || eventState.value.isVariantParent) {
            return null;
        }
        const eventId = eventState.value.id;
        const eventContext: ProductContextModel = {
            product: eventState.value,
            productInfo: eventInfo,
        };
        return (
            <Page data-test-selector={`event-registration_eventId_${eventId}`}>
                <CurrentCategory categoryPathProp={eventPath} postFixBreadcrumb={postfixBreadcrumb}>
                    <ProductContext.Provider value={eventContext}>
                        <Zone contentId={this.props.id} zoneName="Content" />
                    </ProductContext.Provider>
                </CurrentCategory>
            </Page>
        );
    }
}
const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(EventRegistrationPage),
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "System",
    },
};
export default pageModule;
